/* PersonaSwitcher — top-bar locale + currency + selling-entity switcher.

   Extracted from InfoShopPage.jsx in v3.8.5 to start chipping away at
   the >1000-line page component. All `data-testid`s preserved so the
   testing agent + browser automation continue to pass without churn.
*/
import React from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import Flag from "../../components/Flag";
import { PERSONAS, PERSONA_KEYS } from "../../i18n/personas";

const LOCALE_LABEL = {
  en: "English",
  nl: "Nederlands",
  "pt-BR": "Português",
  "zh-CN": "简体中文",
};

export default function PersonaSwitcher({ persona, personaKey, switchPersona, t }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="persona-switcher-trigger"
          className="hidden md:inline-flex items-center gap-2 pl-2.5 pr-2 py-1.5 rounded-md border border-slate-200 bg-white hover:border-[#0072CE] text-slate-700 shrink-0"
        >
          <Flag code={persona.country_code} height={14} className="-mt-0.5" />
          <div className="text-left leading-tight">
            <div className="text-[9px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
              {t("header.demo_persona")}
            </div>
            <div className="text-xs font-semibold text-slate-900">
              {persona.label || persona.display_name}
              <span className="text-slate-400 font-normal"> · {persona.currency}</span>
            </div>
          </div>
          <ChevronDown size={14} className="text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72" data-testid="persona-switcher-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">
          Switch persona · localized demo
        </DropdownMenuLabel>
        {PERSONA_KEYS.map((k) => {
          const opt = PERSONAS[k];
          const active = k === personaKey;
          const localeLabel = LOCALE_LABEL[opt.locale] || opt.locale;
          return (
            <DropdownMenuItem
              key={k}
              data-testid={`persona-option-${k}`}
              onClick={() => switchPersona(k)}
              className={`gap-3 ${active ? "bg-[#0072CE]/5" : ""}`}
            >
              <Flag code={opt.country_code} height={18} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm">{opt.label || opt.display_name}</div>
                <div className="text-[11px] text-slate-500">
                  {opt.display_name} · {opt.currency}
                  <span className="text-slate-300"> · </span>
                  {localeLabel}
                </div>
              </div>
              {active && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#0072CE]">Active</span>
              )}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled className="text-[11px] text-slate-500 leading-snug">
          Switching reloads the storefront in the persona&apos;s locale, converts every price
          to the local currency and asks for fresh contact confirmation.
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
